import { useEffect, useLayoutEffect, useRef } from 'react'
import { useLocation } from 'react-router'
import { isHubPath, readHubReturn, rememberPage, rememberTitle, type HubReturn } from './hubReturn'

/**
 * The title last credited to a page. Module scope, because the app swaps
 * layouts (and so remounts this hook) between discovery pages and the rest.
 */
let creditedTitle = typeof document === 'undefined' ? '' : document.title

/**
 * Records each page you visit outside the account hub, and inside the hub
 * returns the last one as the "Back to …" target. Mount it in the app chrome
 * (the side nav does).
 */
export function useHubReturn(): HubReturn {
  const { pathname, search } = useLocation()
  const showing = useRef(pathname)

  // Layout effects run before the title observer's callback can fire, so a
  // new page's title is never credited to the page before it.
  useLayoutEffect(() => {
    showing.current = pathname
    if (isHubPath(pathname)) return
    // If the page already set its title (its effects can run before ours),
    // it differs from the one credited to the page before.
    const title = document.title !== creditedTitle ? document.title : ''
    if (title) creditedTitle = title
    rememberPage(pathname, search, title)
  }, [pathname, search])

  // Pages set their title after they render (react-helmet): follow it.
  useEffect(() => {
    if (typeof MutationObserver === 'undefined') return
    const observer = new MutationObserver(() => {
      if (document.title === creditedTitle) return
      creditedTitle = document.title
      if (!isHubPath(showing.current)) rememberTitle(showing.current, creditedTitle)
    })
    observer.observe(document.head, { subtree: true, childList: true, characterData: true })
    return () => observer.disconnect()
  }, [])

  // The target lives in sessionStorage; reading it is cheap, so read it on
  // each render. Inside the hub nothing overwrites it, so it holds steady.
  return readHubReturn()
}
